import {ReactElement} from 'react';
import {I18n} from 'aws-amplify/utils';
import {Box} from '@mantine/core';
import {
    ReloadButton,
    SimplePageHeader,
} from '@flexinet/ui-components';

import {useCertificates} from '../hooks/useCertificates';
import {useCertificatesSelection} from '../hooks/useCertificatesSelection';
import {useCertificateRequestConnections} from '../hooks/useCertificateRequestConnections';
import {useCertificateDeletion} from '../hooks/useCertificateDeletion';
import {RequestCertificateButton} from '../buttons/RequestCertificateButton';
import {DeleteCertificateButton} from '../buttons/DeleteCertificateButton';
import {FilterCertificatesButton} from '../buttons/FilterCertificatesButton';
import {DeleteCertificateModal} from './DeleteCertificateModal';
import {showErrorNotification} from '../../../components/ServiceNotifications';

// ⚠️ THE RELOAD IS NOT A CONVENIENCE HERE, it is how an operator asks "has it
// issued yet".
//
// `status` and `expirationDate` are read through to the provider on every
// request and nothing pushes a change to the browser — there is no poller and
// no subscription, deliberately. So a manual refetch is the only way to see a
// certificate move from pending to issued before the cache expires.
const CertificatesReloadButton = () => {
    const {reload} = useCertificates();
    return (
        <Box mx={'s'}>
            <ReloadButton onClick={() => reload()}/>
        </Box>
    );
};

// The toolbar's delete affordance, acting on the table's selection.
//
// ⚠️ HIDDEN, NOT DISABLED, when no connection can write — except at zero
// selection, which stays visible-but-disabled so the toolbar does not jump as
// rows are ticked.
const DeleteCertificateTool = () => {

    const {selectedRecords} = useCertificatesSelection();
    const {connections} = useCertificateRequestConnections();
    const deletion = useCertificateDeletion();

    const nothingSelected = selectedRecords.length === 0;
    const canWrite = connections.length > 0;

    if (!nothingSelected && !canWrite) {
        return null;
    }

    const handleDelete = () => {
        // ⚠️ REFUSES A MULTI-ROW DELETE rather than fanning out.
        //
        // Each certificate carries its own consequence and its own provider
        // answer: one may delete cleanly while the next is refused because it
        // is serving a live distribution. A batch would report one outcome for
        // several, and the in-use refusal — the single most useful thing this
        // flow produces — would be attributed to the wrong certificate or lost
        // entirely. The confirmation names ONE certificate and lists what it
        // covers, and it can only do that honestly for one.
        if (selectedRecords.length > 1) {
            showErrorNotification(
                I18n.get('Delete one certificate at a time. The provider answers per certificate — one may be refused because it is still serving traffic while another deletes cleanly — and a batch would report a single outcome for all of them.'),
            );
            return;
        }
        if (selectedRecords[0]) {
            deletion.requestDelete(selectedRecords[0]);
        }
    };

    return (
        <>
            <DeleteCertificateButton
                count={selectedRecords.length}
                onClick={handleDelete}
            />
            <DeleteCertificateModal
                certificate={deletion.certificate}
                opened={deletion.opened}
                onClose={deletion.cancelDelete}
                onConfirm={deletion.confirmDelete}
                isDeleting={deletion.isDeleting}
                refusal={deletion.refusal}
                error={deletion.error}
            />
        </>
    );
};

export const IndexPageHeader = () => {

    // Request and Delete hide themselves on capability rather than being
    // conditionally rendered here — the decision belongs with the control that
    // knows what it is asking about.
    const tools: ReactElement[] = [
        <RequestCertificateButton key={'request'}/>,
        <DeleteCertificateTool key={'delete'}/>,
        <FilterCertificatesButton key={'filter'}/>,
        <CertificatesReloadButton key={'reload'}/>,
    ];

    return (
        <SimplePageHeader
            title={I18n.get('Certificates')}
            tools={tools}
        />
    );
};
